import { checklistAnswerValue } from '@/services/checklist/checklistObservations';
import { MMKVChecklistValuesV2PreparedStore } from './MMKVChecklistValuesV2PreparedStore';
import { sha512Hex } from './PendingOperationTerminalization';

type Row = Record<string, any>;
export const sameMediaRow = (left: Row | null | undefined, right: Row): boolean => Boolean(left) &&
  [...new Set([...Object.keys(left!), ...Object.keys(right)])].every(key =>
    JSON.stringify(left![key] ?? null) === JSON.stringify(right[key] ?? null));

export const mediaPayloadHash = (payload: Row): string => sha512Hex(JSON.stringify({
  id: payload.id, campo_id: payload.campo_id, snapshot_field_id: payload.snapshot_field_id,
  valor: payload.valor,
  ...(payload.observacao !== undefined ? { observacao: payload.observacao } : {}),
  expected_updated_at: payload.expected_updated_at,
  ...(payload.expected_partner_revision !== undefined ? { expected_partner_revision: payload.expected_partner_revision } : {}),
  idempotency_key: payload.idempotency_key,
}));

export const MEDIA_SUCCESSION_SCHEMA = 'checklist-media-succession/v1';
const preparedStore = new MMKVChecklistValuesV2PreparedStore();

/** A ligação local não é recibo: guarda a tentativa exata antes de substituir sua fila. */
export function captureChecklistMediaSuccession(successor: Row | null, predecessor: Row | null,
  value: Row | undefined | null, operations: Row[], archives: Row[]): Row | null {
  if (!successor || !predecessor || !value || successor.id === predecessor.id ||
      successor.user_id !== predecessor.user_id || successor.empresa_id !== predecessor.empresa_id ||
      successor.item_id !== predecessor.item_id || successor.campo_id !== predecessor.campo_id ||
      (value.valor !== predecessor.prepared_public_url && value.valor !== predecessor.local_uri)) return null;
  for (const operation of operations) {
    if (operation.operation !== 'checklist_partial_update' || operation.collection !== 'guincho_valores_checklist' ||
        operation.item_id !== predecessor.item_id || operation.empresa_id !== predecessor.empresa_id ||
        operation.user_id !== predecessor.user_id) continue;
    let answers: Row;
    try { answers = JSON.parse(operation.data); } catch { continue; }
    const fieldId = Object.keys(answers).find(id => [value.campo_id, value.snapshot_field_id].includes(id) &&
      checklistAnswerValue(answers[id]) === predecessor.prepared_public_url);
    if (!fieldId) continue;
    const prepared = preparedStore.get(operation.id, fieldId);
    if (!prepared?.dispatched || prepared.payload.id !== value.id || prepared.payload.valor !== predecessor.prepared_public_url) continue;
    return successionArchive(successor, predecessor, value, prepared.operation ?? operation, fieldId, prepared.payload, prepared.expectedObservation, prepared.observationBasis);
  }
  // Uma correção ainda não despachada substitui B conservando sua dependência direta de A.
  const inherited = archives.map(archive => readChecklistMediaSuccession(archive, predecessor)).filter(Boolean);
  if (inherited.length === 1) {
    const link = inherited[0]!;
    return successionArchive(successor, link.predecessor, link.value, link.operation, link.fieldId, link.payload, link.expectedObservation, link.observationBasis);
  }
  for (const archive of archives) {
    let data: Row;
    try { data = JSON.parse(archive.data); } catch { continue; }
    if (!['server_applied', 'historical_materialized'].includes(archive.accounting_state) ||
        archive.empresa_id !== predecessor.empresa_id || archive.user_id !== predecessor.user_id ||
        !sameMediaRow(data.media_applied_ack?.pending, predecessor) || data.payload?.id !== value.id ||
        data.payload?.valor !== predecessor.prepared_public_url || mediaPayloadHash(data.payload) !== archive.payload_sha512) continue;
    return successionArchive(successor, predecessor, value, data.media_applied_ack.operation, data.field_id, data.payload, data.media_applied_ack.expected_observation, data.media_applied_ack.observation_basis);
  }
  return null;
}

function successionArchive(successor: Row, predecessor: Row, value: Row, operation: Row, fieldId: string, payload: Row,
  expectedObservation?: string | null, observationBasis?: Row): Row {
  const now = Date.now();
  const data = JSON.stringify({ media_succession: { schema: MEDIA_SUCCESSION_SCHEMA, successor,
    predecessor, value, operation, fieldId, payload, expectedObservation, observationBasis, payloadSha512: mediaPayloadHash(payload) } });
  return { id: `media-succession:${sha512Hex(`${successor.id}:${predecessor.id}`).slice(0, 48)}`,
    empresa_id: successor.empresa_id, user_id: successor.user_id, operation: 'checklist_media_succession',
    collection: 'guincho_valores_checklist', item_id: successor.item_id, data, accounting_state: 'unaccounted',
    reason: MEDIA_SUCCESSION_SCHEMA, original_timestamp: now, payload_sha512: sha512Hex(data),
    accounted_at: now, created_at: now, updated_at: now };
}

export function readChecklistMediaSuccession(archive: Row, pending: Row): Row | null {
  let link: Row;
  try { link = JSON.parse(archive.data).media_succession; } catch { return null; }
  if (archive.operation !== 'checklist_media_succession' || archive.accounting_state !== 'unaccounted' ||
      archive.payload_sha512 !== sha512Hex(archive.data) || link?.schema !== MEDIA_SUCCESSION_SCHEMA ||
      !link.successor || !link.predecessor || !link.payload || !link.value ||
      archive.empresa_id !== pending.empresa_id || archive.user_id !== pending.user_id || archive.item_id !== pending.item_id ||
      link.successor.id !== pending.id || ['item_type', 'item_id', 'campo_id', 'local_uri', 'bucket', 'path', 'user_id', 'empresa_id']
        .some(key => link.successor[key] !== pending[key]) ||
      link.predecessor.prepared_public_url !== link.payload?.valor || link.payload.id !== link.value.id ||
      mediaPayloadHash(link.payload) !== link.payloadSha512 || !link.operation?.id || !link.fieldId) return null;
  return link;
}

export function confirmsChecklistMediaPredecessor(link: Row, evidence: Row): boolean {
  let data: Row;
  try { data = JSON.parse(evidence.data); } catch { return false; }
  return Boolean(data.payload) && ['server_applied', 'historical_materialized'].includes(evidence.accounting_state) &&
    evidence.operation === 'checklist_partial_update' && evidence.collection === 'guincho_valores_checklist' &&
    evidence.empresa_id === link.predecessor.empresa_id && evidence.user_id === link.predecessor.user_id &&
    evidence.item_id === link.predecessor.item_id && evidence.original_timestamp === link.operation.timestamp &&
    data.field_id === link.fieldId && data.idempotency_key === link.payload.idempotency_key &&
    mediaPayloadHash(data.payload) === evidence.payload_sha512 && evidence.payload_sha512 === link.payloadSha512 &&
    JSON.stringify(data.payload) === JSON.stringify(link.payload);
}

/** O conflito original continua imutável quando sua sucessora recebe o baseline confirmado de A. */
export function hasConfirmedMediaSuccessionBaseline(pending: Row, payload: Row, archives: Row[]): boolean {
  const links = archives.map(archive => readChecklistMediaSuccession(archive, pending)).filter(Boolean);
  return links.length === 1 && links[0]!.payload.id === payload.id && payload.valor === pending.prepared_public_url &&
    payload.expected_updated_at === links[0]!.successor.expected_remote_updated_at &&
    pending.expected_remote_value === links[0]!.predecessor.prepared_public_url &&
    archives.some(evidence => confirmsChecklistMediaPredecessor(links[0]!, evidence));
}

/** A leitura da primeira tentativa só prova nota omitida se pertencer ao CAS exato enviado. */
export function predecessorObservation(link: Row): string | null | undefined {
  if (Object.prototype.hasOwnProperty.call(link.payload, 'observacao')) return link.payload.observacao;
  const basis = link.observationBasis;
  if (!basis || basis.id !== link.payload.id || basis.empresa_id !== link.predecessor.empresa_id ||
      basis.checklist_id !== link.predecessor.item_id ||
      (basis.campo_id ?? null) !== (link.payload.campo_id ?? null) ||
      (basis.snapshot_field_id ?? null) !== (link.payload.snapshot_field_id ?? null)) return undefined;
  // A inserção aplicada com CAS nulo e ausência comprovada cria a nota nula padrão.
  if (basis.absent === true) return link.payload.expected_updated_at === null ? null : undefined;
  return basis.updated_at === link.payload.expected_updated_at &&
    Object.prototype.hasOwnProperty.call(basis, 'observacao') ? basis.observacao : undefined;
}

/** Exceção restrita à nota do pedido B terminal, ligada à tentativa A comprovada. */
export function authorizedSuccessorObservation(pending: Row, value: Row, payload: Row, archives: Row[], remote?: Row): boolean {
  if (!Object.prototype.hasOwnProperty.call(payload, 'observacao') ||
      (value.observacao ?? null) !== payload.observacao || !hasConfirmedMediaSuccessionBaseline(pending, payload, archives)) return false;
  const link = archives.map(row => readChecklistMediaSuccession(row, pending)).find(Boolean)!;
  const note = predecessorObservation(link);
  if (!Object.prototype.hasOwnProperty.call(link.payload, 'observacao') && !archives.some(evidence => {
    if (!confirmsChecklistMediaPredecessor(link, evidence)) return false;
    try { return JSON.stringify(JSON.parse(evidence.data).media_applied_ack?.observation_basis) === JSON.stringify(link.observationBasis); }
    catch { return false; }
  })) return false;
  return note !== undefined && (!remote || remote.valor === link.payload.valor &&
    remote.id === value.id && remote.empresa_id === pending.empresa_id && remote.checklist_id === pending.item_id &&
    (remote.campo_id ?? null) === (link.payload.campo_id ?? null) &&
    (remote.snapshot_field_id ?? null) === (link.payload.snapshot_field_id ?? null) &&
    Object.prototype.hasOwnProperty.call(remote, 'observacao') && remote.observacao === note);
}

/** Remove somente a intenção exata já representada pelo conflito original de B. */
export function withoutSuccessorObservationIntent(operations: Row[], conflicts: Row[], conflict: Row, payload: Row,
  authorized: boolean, fields: unknown[]): { operations: Row[]; conflicts: Row[] } {
  if (!authorized) return { operations, conflicts };
  return { conflicts: conflicts.filter(row => !(row.id === conflict.id && row.payload_sha512 === mediaPayloadHash(payload) &&
      row.payload === JSON.stringify(payload))),
    operations: operations.filter(row => {
      if (row.id !== conflict.pending_operation_id || row.empresa_id !== conflict.empresa_id || row.user_id !== conflict.user_id ||
          row.item_id !== conflict.item_id || row.operation !== 'checklist_partial_update' || row.collection !== conflict.collection) return true;
      try {
        const answers = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
        const targeted = Object.keys(answers).filter(id => fields.includes(id));
        const answer = answers[targeted[0]];
        return targeted.length !== 1 || checklistAnswerValue(answer) !== payload.valor ||
          !Object.prototype.hasOwnProperty.call(answer ?? {}, 'observacao') || answer.observacao !== payload.observacao;
      } catch { return true; }
    }) };
}

/** O envio posterior aplicado encerra apenas o conflito B que preservou aquela nota anterior. */
export function planSupersededMediaNoteConflict(pending: Row, value: Row, remote: Row, archives: Row[], conflicts: Row[],
  operations: Row[], parent: Row | null | undefined, tombstones: Row[], images: Row[]): { archive: Row; conflict: Row; data: string } | null {
  const ids = [pending.item_id, pending.campo_id, value.id, value.campo_id, value.snapshot_field_id, parent?.template_id].filter(Boolean);
  if (!parent || parent.empresa_id !== pending.empresa_id || parent.retired_at ||
      images.filter(row => row.item_type === 'CHECKLIST' && row.item_id === pending.item_id && ids.includes(row.campo_id)).length !== 1 ||
      tombstones.some(row => row.empresa_id === pending.empresa_id && ids.includes(row.entity_id)) ||
      operations.some(row => row.operation === 'delete' && ids.includes(row.item_id)) ||
      conflicts.some(row => row.empresa_id === pending.empresa_id && row.item_id === pending.item_id &&
        row.reason === 'checklist-create-delete-cancellation/v1' && row.status !== 'resolved') ||
      remote.id !== value.id || remote.empresa_id !== pending.empresa_id || remote.checklist_id !== pending.item_id ||
      (remote.campo_id ?? null) !== (value.campo_id ?? null) ||
      (remote.snapshot_field_id ?? null) !== (value.snapshot_field_id ?? null) ||
      typeof remote.updated_at !== 'string' || !Number.isFinite(Date.parse(remote.updated_at)) ||
      remote.valor !== pending.prepared_public_url || remote.observacao !== (value.observacao ?? null)) return null;
  const matches: Array<{ archive: Row; conflict: Row; data: string }> = [];
  for (const conflict of conflicts) {
    if (!['open', 'blocked'].includes(conflict.status) || conflict.empresa_id !== pending.empresa_id ||
        conflict.user_id !== pending.user_id || conflict.item_id !== pending.item_id ||
        conflict.collection !== 'guincho_valores_checklist' ||
        !['optimistic_lock_failed', 'expected_updated_at_mismatch'].includes(conflict.reason)) continue;
    let payload: Row;
    try { payload = JSON.parse(conflict.payload); } catch { continue; }
    const archive = archives.find(row => row.id === conflict.id.replace(/^conflict:/, 'accounted:'));
    let original: Row;
    try { original = JSON.parse(archive?.data); } catch { continue; }
    if (!archive || archive.accounting_state !== 'conflict_materialized' || archive.reason !== conflict.reason ||
        archive.user_id !== conflict.user_id || archive.empresa_id !== conflict.empresa_id ||
        archive.operation !== 'checklist_partial_update' || archive.collection !== conflict.collection ||
        archive.item_id !== conflict.item_id || archive.payload_sha512 !== conflict.payload_sha512 ||
        mediaPayloadHash(payload) !== conflict.payload_sha512 || JSON.stringify(original.payload) !== JSON.stringify(payload) ||
        original.idempotency_key !== payload.idempotency_key || original.field_id !== conflict.field_id ||
        !payload.idempotency_key?.startsWith(`syncv2:${conflict.pending_operation_id}:`) ||
        !conflict.id.startsWith(`conflict:${conflict.pending_operation_id}:`) ||
        conflict.id.split(':').at(-1) !== conflict.payload_sha512.slice(0, 24) ||
        !authorizedSuccessorObservation(pending, { ...value, observacao: payload.observacao }, payload, archives) ||
        payload.observacao === remote.observacao) continue;
    const applied = archives.find(row => {
      let data: Row;
      try { data = JSON.parse(row.data); } catch { return false; }
      const sent = data.payload;
      const dispatched = data.media_applied_ack;
      return ['server_applied', 'historical_materialized'].includes(row.accounting_state) &&
        row.operation === archive.operation && row.collection === archive.collection && row.item_id === archive.item_id &&
        row.empresa_id === archive.empresa_id && row.user_id === archive.user_id &&
        row.original_timestamp >= archive.original_timestamp && dispatched?.operation?.id === conflict.pending_operation_id &&
        dispatched?.operation?.timestamp === row.original_timestamp && sent?.id === value.id &&
        sent.valor === pending.prepared_public_url && sent.observacao === remote.observacao &&
        sent.idempotency_key !== payload.idempotency_key && data.idempotency_key === sent.idempotency_key &&
        mediaPayloadHash(sent) === row.payload_sha512 && sent.expected_updated_at === pending.expected_remote_updated_at &&
        (sent.campo_id ?? null) === (remote.campo_id ?? null) &&
        (sent.snapshot_field_id ?? null) === (remote.snapshot_field_id ?? null) &&
        ['id', 'item_id', 'campo_id', 'local_uri', 'path', 'bucket', 'user_id', 'empresa_id', 'prepared_public_url']
          .every(key => dispatched.pending?.[key] === pending[key]);
    });
    if (!applied) continue;
    const appliedPayload = JSON.parse(applied.data).payload;
    matches.push({ archive, conflict, data: JSON.stringify({ ...original, media_note_successor: {
      schema: 'checklist-media-note-successor/v1', pendingId: pending.id, appliedArchiveId: applied.id,
      originalPayloadSha512: archive.payload_sha512, appliedPayloadSha512: applied.payload_sha512,
      appliedPayload, confirmedRemote: remote,
    } }) });
  }
  return matches.length === 1 ? matches[0] : null;
}
